import { motion } from "framer-motion";
import { Activity, Trophy, Users, Bike, ArrowUpRight, Flame } from "lucide-react";

const STATS = [
  { value: "2016", label: "Création du club LCPS", icon: <Trophy size={16} /> },
  { value: "60+", label: "Adhérents au plus fort", icon: <Users size={16} /> },
  { value: "36'45", label: "Record 10 km", icon: <Activity size={16} /> },
  { value: "Vélo + Fitness", label: "2 sections créées", icon: <Bike size={16} /> },
];

const ARTICLES = [
  {
    title: "« Adrien Sebert, l'orange vitaminé »",
    media: "Actu.fr · L'Orne Hebdo",
    quote:
      "Le sport, c'est une drogue. Un besoin. Je me sens appelé à courir.",
    url: "https://actu.fr/normandie/la-chapelle-pres-sees_61098/sport-adrien-sebert-lorange-vitamine_7228846.html",
  },
  {
    title: "Adrien Sebert quitte la présidence du club qu'il a créé",
    media: "Actu.fr · L'Orne Hebdo",
    quote:
      "Réunir des athlètes, construire un club dynamique, allier amour du sport et plaisir partagé. Mission accomplie.",
    url: "https://actu.fr/normandie/la-chapelle-pres-sees_61098/les-coureurs-pres-sees-adrien-sebert-quitte-la-presidence-et-le-club-qu-il-a-cree_34083595.html",
  },
];

export const SportSection = () => {
  return (
    <section
      id="sport"
      data-testid="sport-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[var(--bg-surface)] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,45,156,0.10),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.10),transparent_60%)]" />

      <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-6"
        >
          <span className="eyebrow inline-flex items-center gap-2">
            <Flame size={13} /> Athlète & créateur
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Le tempérament d'un athlète,
            <br />
            <span className="aurora-text-gradient">au service de votre soirée.</span>
          </h2>
          <p className="mt-5 text-zinc-300 leading-relaxed">
            Coureur passionné et ancien cycliste, j'ai créé en{" "}
            <span className="text-white font-semibold">2016 le club Les Coureurs-près-Sées</span>,
            que j'ai présidé pendant plus de quatre ans avant de passer la main.
          </p>
          <p className="mt-3 text-zinc-400 leading-relaxed text-sm md:text-base">
            Plus de 60 adhérents, des sections vélo et fitness, l'organisation des
            Foulées chapeloises, deux cross à La Chapelle-près-Sées… Le sport m'a forgé
            une discipline, une endurance et un sens du collectif que je transpose à
            chaque événement : préparation millimétrée, rigueur du jour J et
            résistance jusqu'à la dernière chanson.
          </p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`sport-stat-${i}`}
                className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
              >
                <div className="text-[#FF6FB5] mb-2">{s.icon}</div>
                <div className="text-lg md:text-xl font-bold text-white leading-tight">
                  {s.value}
                </div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-400 mt-1 leading-snug">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-6 space-y-4"
        >
          {ARTICLES.map((a, i) => (
            <motion.a
              key={a.title}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`sport-article-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group block rounded-2xl glass p-6 md:p-7 hover:border-[#FF2D9C]/30 transition-all hover:-translate-y-0.5"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#FF6FB5] font-semibold">
                    {a.media}
                  </div>
                  <div className="mt-2 text-base md:text-lg font-semibold text-white leading-snug">
                    {a.title}
                  </div>
                  <p className="mt-3 text-sm text-zinc-400 italic leading-relaxed border-l-2 border-[#FF2D9C]/40 pl-3">
                    « {a.quote} »
                  </p>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-zinc-500 group-hover:text-[#FF6FB5] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0"
                />
              </div>
            </motion.a>
          ))}

          <div className="rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/[0.05] to-transparent p-6 md:p-7">
            <div className="text-xs tracking-[0.22em] uppercase text-[#D4AF37] font-semibold mb-2">
              Ce que ça change pour vous
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Un DJ qui boucle des compétitions à 13 h, mixe à 22 h et tient jusqu'à
              5 h du matin. Une endurance, une régularité et un sang-froid testés sur
              les routes — et mis au service de votre événement.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
